/**
 * Serializes a Map to a JSON-compatible format.
 * Maps are converted to an object with a special `isMap` marker and entries array.
 *
 * @param value The value to serialize
 * @returns The serialized value
 */
export function serializeWithMapSupport<T>(value: T): string {
  return JSON.stringify(value, (_, val: unknown) => {
    if (val instanceof Map) {
      return {
        isMap: true,
        entries: Array.from(val.entries()),
      };
    }
    return val;
  });
}

/**
 * Deserializes a value that may contain Maps from JSON.
 * Objects with `isMap` marker are converted back to Map instances.
 *
 * @param jsonString The JSON string to deserialize
 * @returns The deserialized value with Maps restored
 */
export function deserializeWithMapSupport<T>(jsonString: string): T {
  return JSON.parse(jsonString, (_, val) => {
    if (
      val &&
      typeof val === "object" &&
      val.isMap === true &&
      Array.isArray(val.entries)
    ) {
      return new Map(val.entries);
    }
    return val;
  }) as T;
}

/**
 * Checks if a value contains Maps that need special serialization.
 *
 * @param value The value to check
 * @returns True if the value contains Maps
 */
export function containsMaps(value: unknown): boolean {
  if (value instanceof Map) {
    return true;
  }

  if (value && typeof value === "object") {
    if (Array.isArray(value)) {
      return value.some((item) => containsMaps(item));
    }

    return Object.values(value).some((val) => containsMaps(val));
  }

  return false;
}
